import * as XLSX from 'xlsx';
import { PricingSheetData } from './pricingSheetData';

const formatCurrency = (n: number) =>
  `$${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export function downloadPricingSheetExcel(data: PricingSheetData): void {
  const rows: (string | number | undefined)[][] = [];

  // Title block
  rows.push(['CRC Evans - DATA 360 Pricing Proposal']);
  rows.push(['Reference:', data.referenceNumber]);
  rows.push(['Date:', data.date]);
  rows.push([]);

  // Two-column customer / project section
  rows.push(['PREPARED FOR', '', 'PROJECT DETAILS', '']);

  const customerRows: [string, string][] = [];
  if (data.customerName) customerRows.push(['Customer:', data.customerName]);
  if (data.customerAttn) customerRows.push(['Attention:', data.customerAttn]);
  if (data.customerEmail) customerRows.push(['Email:', data.customerEmail]);
  if (data.customerPhone) customerRows.push(['Phone:', data.customerPhone]);
  if (data.addressLine1) customerRows.push(['Address:', data.addressLine1]);
  if (data.addressLine2) customerRows.push(['', data.addressLine2]);
  const cityLine = [data.city, data.state, data.postalCode].filter(Boolean).join(', ');
  if (cityLine) customerRows.push(['', cityLine]);
  if (data.country) customerRows.push(['', data.country]);

  const projectRows: [string, string][] = [];
  if (data.projectTitle) projectRows.push(['Project:', data.projectTitle]);
  if (data.startDate) projectRows.push(['Start Date:', data.startDate]);
  if (data.endDate) projectRows.push(['End Date:', data.endDate]);
  if (data.durationLabel) projectRows.push(['Duration:', data.durationLabel]);
  if (data.paymentTerms) projectRows.push(['Payment:', data.paymentTerms]);
  if (data.validityPeriod) projectRows.push(['Valid for:', data.validityPeriod]);

  const maxInfoRows = Math.max(customerRows.length, projectRows.length);
  for (let i = 0; i < maxInfoRows; i++) {
    const left = customerRows[i] ?? ['', ''];
    const right = projectRows[i] ?? ['', ''];
    rows.push([left[0], left[1], right[0], right[1]]);
  }

  rows.push([]);

  // Products table header
  rows.push(['Product / Service', 'Configuration', 'Amount']);

  // Product rows — each configuration line gets its own row under the product
  data.products.forEach(row => {
    row.configuration.forEach((line, lineIdx) => {
      if (lineIdx === 0) {
        // First line: product name, first config line, amount
        rows.push([row.productName, line, formatCurrency(row.amount)]);
      } else {
        // Subsequent lines: blank product name and amount, just config line
        rows.push(['', line, '']);
      }
    });
  });

  // Total row
  rows.push(['', 'TOTAL', formatCurrency(data.grandTotal)]);
  rows.push([]);

  // Terms
  rows.push(['TERMS & CONDITIONS']);
  if (data.validityPeriod) {
    rows.push([`This proposal is valid for ${data.validityPeriod}.`]);
  }
  if (data.paymentTerms) {
    rows.push([`Payment terms: ${data.paymentTerms}.`]);
  }
  rows.push(['All prices are in USD and exclude applicable taxes.']);
  rows.push(['This document is confidential and intended solely for the named recipient.']);

  const ws = XLSX.utils.aoa_to_sheet(rows);
  ws['!cols'] = [{ wch: 28 }, { wch: 55 }, { wch: 18 }, { wch: 24 }];

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Pricing Proposal');
  XLSX.writeFile(wb, `Pricing-Proposal-${data.referenceNumber}.xlsx`);
}
